import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import { EntityDocumentsSection } from "../../components/EntityDocumentsSection";
import {
  ApiError,
  getSupplierById,
  updateSupplier,
  uploadSupplierLogo,
  type Supplier,
  type SupplierPayload,
} from "../../fornecedores/fornecedoresApi";
import { APP_LOCALE } from "../../config/locale";

const CONTRACT_DOCUMENT_CATEGORIES = [
  "Empreitada",
  "Subempreitada",
  "Fornecimento",
  "Arrendamento",
  "Prestação de serviços",
  "Outro",
];

type CategoriaFornecedor =
  | "Materiais"
  | "Serviços"
  | "Equipamentos";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar fornecedores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o fornecedor.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar fornecedores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o fornecedor.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarFornecedorView() {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [supplierSnapshot, setSupplierSnapshot] =
    useState<Supplier | null>(null);
  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState<
    CategoriaFornecedor | ""
  >("");
  const [nif, setNif] = useState("");
  const [pessoaContacto, setPessoaContacto] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [condicoes, setCondicoes] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(
    null,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<
    string | null
  >(null);

  useEffect(() => {
    let isMounted = true;

    const loadSupplier = async () => {
      if (!supplierId) {
        setLoadError("Fornecedor inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(supplierId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Fornecedor inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getSupplierById(parsedId);
        if (!isMounted) return;

        setSupplierSnapshot(response);
        setNome(response.name ?? "");
        setNif(response.taxId ?? "");
        setCategoria(
          (response.category as CategoriaFornecedor) ?? "",
        );
        setPessoaContacto(response.contactName ?? "");
        setEmail(response.email ?? "");
        setTelefone(response.phoneNumber ?? "");
        setCondicoes(response.commercialTerms ?? "");
        setLogoUrl(response.logoUrl ?? null);
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSupplier();

    return () => {
      isMounted = false;
    };
  }, [supplierId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!supplierSnapshot) {
      setSubmitError("Fornecedor inválido.");
      return;
    }

    if (!nome.trim()) {
      setSubmitError("O nome do fornecedor é obrigatório.");
      return;
    }

    if (!categoria) {
      setSubmitError("A categoria do fornecedor é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: SupplierPayload = {
      name: nome.trim(),
      taxId: nif.trim() || null,
      category: categoria,
      contactName: pessoaContacto.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      commercialTerms: condicoes.trim() || null,
      notes: supplierSnapshot.notes ?? null,
      purchasesCount: supplierSnapshot.purchasesCount,
    };

    try {
      await updateSupplier(supplierSnapshot.id, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (logoFile) {
      try {
        await uploadSupplierLogo(supplierSnapshot.id, logoFile);
      } catch {
        setSubmitError(
          "Fornecedor atualizado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/fornecedores", {
      state: { toast: "Fornecedor atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/fornecedores")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-fornecedor-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Fornecedor"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Fornecedor"
        subtitle="Atualize os dados do fornecedor e informações de contacto."
        actions={actions}
        backButton={{ onClick: () => navigate("/fornecedores") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-fornecedor-form" onSubmit={handleSubmit}>
            <div className="max-w-3xl mx-auto space-y-6 mb-20">
              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação Principal
                </h3>

                <AvatarUploader
                  currentImage={logoUrl}
                  onImageChange={(file) => setLogoFile(file)}
                  label="Logotipo do Fornecedor"
                  subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                  size="medium"
                />

                <div>
                  <label
                    htmlFor="nomeFornecedor"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Nome do Fornecedor{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeFornecedor"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Insira o nome do fornecedor"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="categoria"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Categoria{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="categoria"
                      value={categoria}
                      onChange={(e) =>
                        setCategoria(
                          e.target.value as CategoriaFornecedor,
                        )
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading || isSaving}
                    >
                      <option value="">
                        Selecione a categoria
                      </option>
                      <option value="Materiais">Materiais</option>
                      <option value="Serviços">Serviços</option>
                      <option value="Equipamentos">
                        Equipamentos
                      </option>
                    </select>
                    <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="nif"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    NIF
                  </label>
                  <input
                    id="nif"
                    type="text"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder="Número de Identificação Fiscal"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

              </div>

              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto
                </h3>

                <div>
                  <label
                    htmlFor="pessoaContacto"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Pessoa de contacto
                  </label>
                  <input
                    id="pessoaContacto"
                    type="text"
                    value={pessoaContacto}
                    onChange={(e) =>
                      setPessoaContacto(e.target.value)
                    }
                    placeholder="Nome da pessoa de contacto"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contacto@email.pt"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Telefone
                  </label>
                  <input
                    id="telefone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="+351 912 345 678"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="condicoes"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Condições Comerciais
                  </label>
                  <input
                    id="condicoes"
                    type="text"
                    value={condicoes}
                    onChange={(e) => setCondicoes(e.target.value)}
                    placeholder="Ex: 30 dias, 45 dias, Pronto pagamento"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>
              </div>
            </div>
          </form>

          {supplierSnapshot && (
            <div className="mt-6">
              <EntityDocumentsSection
                ownerType="supplier"
                ownerId={supplierSnapshot.id}
                section="contract"
                title="Contratos e Documentos"
                variant="card"
                showCounterparty
                categories={CONTRACT_DOCUMENT_CATEGORIES}
                emptyLabel="Sem contratos ou documentos arquivados."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
